import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService, HttpRequestModel } from 'src/app/service/httpservice/http.service';
import { JobPointeSearchParams } from 'src/app/shared/models/jobPointeSearchParams';
import { buildQueryString } from 'src/app/utils/functions';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class JobPointeSettingsService {
  HttpRequestModel: HttpRequestModel = new HttpRequestModel();

  constructor(
    private httpService: HttpService,
    private authService: AuthService,
  ) {}

  private defaultParams: JobPointeSearchParams = {
    page: 1,
    limit: 1000,
    search: '',
    fields: '',
    order: 'toolId,DESC',
  };
  defaultShopParams: JobPointeSearchParams = {
    page: 1,
    limit: 1000,
    search: '',
    fields: '',
  };

  private activeEmployees = {
    order: 'employee_alias ASC', // this format is not wrong, it is using v1 endpoints
    status: '1',
  };

  private employeesParams = {
    attributes: ['id', 'employeeAlias'],
    limit: 1000,
    order: 'employeeAlias',
    'scope[employeeAlias]': true,
  };

  private jobParams = {
    attributes: 'jobNumber,name',
    order: 'name,ASC',
  };
  private facilityParams = {
    attributes: 'id,facilityName,ownerId',
    order: 'facilityName,ASC',
    limit: 1000,
  };
  private jobCityParams = {
    attributes: 'city',
    group: 'city',
    limit: 1000,
  };
  private projectManagerParams = {
    'scope[roleName]': ['Project Managers', 'Executive'],
    attributes: ['id', 'employeeAlias'],
    order: 'employeeAlias',
    embed: 'role',
    limit: 1000,
  };
  private jobAssistantParams = {
    attributes: ['assistant'],
    order: 'assistant',
    group: 'assistant',
    limit: 1000,
  };
  private jobForemanParams = {
    attributes: ['foreman'],
    order: 'foreman',
    group: 'foreman',
    limit: 1000,
  };

  private ownerParams = {
    limit: 1000,
  };

  // READ endpoints
  // READ endpoints should accept JobPointeSearchParams as input. This input should override a set of defaults assigned to the GET endpoint
  read = {
    departments: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.request('GET', `departments`, { ...params }),
    roles: (params): Observable<any> => this.request('POST', `roles`, params),
    employeeTitles: (params): Observable<any> => this.request('GET', `employee_titles`, params),
    employeeGroups: (params): Observable<any> => this.request('GET', `employee_groups`, params),
    locals: (params): Observable<any> => this.request('GET', `locals`, params),
    jobs: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.request('GET', `jobs/v2`, { ...this.jobParams, ...params }),
    jobCities: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.request('GET', `jobs/v2`, { ...this.jobCityParams, ...params }),
    projectManagers: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.request('GET', `employees/v2`, {
        ...this.projectManagerParams,
        ...params,
      }),
    employees: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.request('GET', `employees/v2`, {
        ...this.employeesParams,
        ...params,
      }),
    jobAssistants: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.request('GET', `jobs/v2Filters`, {
        ...this.jobAssistantParams,
        ...params,
        attribute: 'project_assistant_name',
        key: 'assistant',
        start: 0,
        limit: 1000,
      }),
    jobForeman: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.request('GET', `jobs/v2Filters`, {
        ...this.jobForemanParams,
        ...params,
        attribute: 'project_foreman_name',
        key: 'foreman',
        start: 0,
        limit: 1000,
      }),
    owner: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.request('GET', `owner/v2`, { ...this.ownerParams, ...params }),
    facility: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.request('GET', `owner_and_facilities/v2`, {
        ...this.facilityParams,
        ...params,
      }),
    employeeUnionClasses: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.request('GET', `employeeUnionClasses`, { ...params }),
    jobBoardAttributes: (params): Observable<any> =>
      this.request('GET', `jobpointe/job_board_attributes`, { ...params }),
    jobBoardTags: (params): Observable<any> => this.request('GET', `jobpointe/job_board_tags`, { ...params }),
  };

  // CREATE endpoints
  create = {};

  // UPDATE endpoints
  update = {
    'asset-classes': (payload: any = {}): Observable<any> =>
      this.request('PUT', `tools/assets_classes/${payload.id}`, payload.data),
    manufacturers: (payload: any = {}): Observable<any> =>
      this.request('PUT', `tools/manufacturers/${payload.id}`, payload.data),
    categories: (payload: any = {}): Observable<any> =>
      this.request('PUT', `tools/categories/${payload.id}`, payload.data),
    'shop-locations': (payload: any = {}): Observable<any> =>
      this.request('PUT', `shop_locations/${payload.id}`, payload.data),
    alert: (payload: any = {}): Observable<any> => this.request('PUT', `tools/alerts/${payload.id}`, payload.data),
    vehicle_details: (payload: any = {}, id: string): Observable<any> =>
      this.request('PUT', `tools/vehicle_details/${id}`, payload),
  };

  // ENABLE/DISABLE asset_class
  status = {
    'asset-classes': (payload: any): Observable<any> =>
      this.request('PATCH', `tools/assets_classes/${payload.id}`, {
        enabled: payload.data.enabled,
      }),
    manufacturers: (payload: any): Observable<any> =>
      this.request('PATCH', `tools/manufacturers/${payload.id}`, {
        enabled: payload.data.enabled,
      }),
    categories: (payload: any): Observable<any> =>
      this.request('PATCH', `tools/categories/${payload.id}`, {
        enabled: payload.data.enabled,
      }),
    'shop-locations': (payload: any): Observable<any> =>
      this.request('PATCH', `shop_locations/${payload.id}`, {
        enabled: payload.data.enabled,
      }),
  };

  // DELETE endpoints
  delete = {
    'asset-classes': (id: number): Observable<any> => this.request('DELETE', `tools/assets_classes/${id}`),
    manufacturers: (id: number): Observable<any> => this.request('DELETE', `tools/manufacturers/${id}`),
    categories: (id: number): Observable<any> => this.request('DELETE', `tools/categories/${id}`),
    'shop-locations': (id: number): Observable<any> => this.request('DELETE', `shop_locations/${id}`),
  };

  private request(
    requestType: string,
    url: string,
    data: any = '',
    showLoader: boolean = false,
    hideLoader: boolean = false,
  ): Observable<any> {
    // if GET request and param data was received, format query string and reset data to be sent in body
    if (requestType === 'GET' && data) {
      const queryString = buildQueryString(data);
      url += queryString;
      data = '';
    }

    this.HttpRequestModel.reset();
    this.HttpRequestModel.method = requestType;
    this.HttpRequestModel.url = url;
    this.HttpRequestModel.body = data;
    return this.httpService.init(this.HttpRequestModel, showLoader, hideLoader).pipe(map(response => response));
  }
}
